import { motion } from 'framer-motion';
import { Check, DollarSign, Mail, X } from 'lucide-react';
import { useModal } from '../../hooks/modalContext';
import Pagination from '../../UI/Pagination/Pagination';
import { useEffect, useState } from 'react';
import { referralListResponse } from '../../utils/types';
import { getReferralList } from '../../../auth/_requests';
import { formatDateToDisplayShortMonth, getPaginationRange } from '../../utils/utils';
import { useAuth } from '../../../auth/Auth';

const ReferralHistory = () => {
  const { auth } = useAuth();
  const { setModal } = useModal();
  const [referralList, setReferralList] = useState<referralListResponse['data']>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 4;
  const totalPages = Math.ceil(totalCount / limit);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchReferralList = async () => {
      setLoading(true);
      try {
        const response = await getReferralList(auth?.data.token);
        setReferralList(response.data);
        setTotalCount(response.meta.totalCount);
      } catch (err) {
        console.error('Error fetching referral list');
      } finally {
        setLoading(false);
      }
    };
    fetchReferralList();
  }, []);

  const handlePageClick = (page: number | string) => {
    if (typeof page === 'number' && page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <motion.div
      className='relative rounded-lg shadow-xl w-full max-w-[95vw] sm:max-w-[600px] md:max-w-[700px] lg:max-w-[800px] bg-white flex flex-col overflow-auto max-h-[90vh]'
      initial='hidden'
      animate='visible'
      exit='exit'
    >
      <div className='flex justify-end p-4'>
        <X className='cursor-pointer' size={24} onClick={() => setModal(null)} />
      </div>

      <div className='w-full overflow-x-auto px-4 sm:px-8 mb-4'>
        <h2 className='text-[20px]  mb-10 text-main'>Referral activity</h2>

        {/* Table for larger screens */}
        <table className='hidden sm:table min-w-full text-sm text-left border-collapse'>
          <thead>
            <tr className='border-b text-gray-500'>
              <th className='px-4 py-2'>Date referred</th>
              <th className='px-4 py-2'>Email</th>
              <th className='px-4 py-2 text-center'>Invited</th>
              <th className='px-4 py-2 text-center'>Signed up</th>
              <th className='px-4 py-2 text-center'>Qualified</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 4 }).map((_, index) => (
                  <tr key={index} className='border-b last:border-0'>
                    <td className='px-4 py-3'>
                      <div className='h-4 w-24 bg-gray-200 animate-pulse rounded'></div>
                    </td>
                    <td className='px-4 py-3'>
                      <div className='h-4 w-32 bg-gray-200 animate-pulse rounded'></div>
                    </td>
                    <td className='px-4 py-3 text-center'>
                      <div className='w-8 h-8 rounded-full bg-gray-200 animate-pulse mx-auto'></div>
                    </td>
                    <td className='px-4 py-3 text-center'>
                      <div className='w-8 h-8 rounded-full bg-gray-200 animate-pulse mx-auto'></div>
                    </td>
                    <td className='px-4 py-3 text-center'>
                      <div className='w-8 h-8 rounded-full bg-gray-200 animate-pulse mx-auto'></div>
                    </td>
                  </tr>
                ))
              : referralList.map((referral) => {
                  const maskedEmail = referral.email.replace(
                    /(.{1}).*(@.).*/,
                    (_, a, b) => `${a}****${b}****`
                  );
                  const invited = referral.isInvited;
                  const signedUp = referral.isSignedUp;
                  const qualified = referral.isQualified;

                  return (
                    <tr key={referral.referralId} className='border-b last:border-0 text-gray-800'>
                      <td className='px-4 py-3'>
                        {referral.invitedOn
                          ? formatDateToDisplayShortMonth(referral.invitedOn)
                          : formatDateToDisplayShortMonth(referral.timestampAdded)}
                      </td>
                      <td className='px-4 py-3'>{maskedEmail}</td>
                      <td className='px-4 py-3 text-center'>
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto ${
                            invited
                              ? 'bg-[var(--primary-color)] text-white'
                              : 'bg-gray-200 text-gray-400'
                          }`}
                        >
                          <Mail size={16} />
                        </div>
                      </td>
                      <td className='px-4 py-3 text-center'>
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto ${
                            signedUp
                              ? 'bg-[var(--primary-color)] text-white'
                              : 'bg-gray-200 text-gray-400'
                          }`}
                        >
                          <Check size={16} />
                        </div>
                      </td>
                      <td className='px-4 py-3 text-center'>
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto ${
                            qualified
                              ? 'bg-[var(--primary-color)] text-white'
                              : 'bg-gray-200 text-gray-400'
                          }`}
                        >
                          <DollarSign size={16} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>

        {/* Card layout for smaller screens */}
        <div className='sm:hidden flex flex-col gap-4'>
          {loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className='border p-4 rounded-lg shadow-sm animate-pulse space-y-2'
                >
                  <div className='h-4 w-32 bg-gray-200 rounded'></div>
                  <div className='h-4 w-48 bg-gray-200 rounded'></div>
                  <div className='flex gap-2'>
                    <div className='w-8 h-8 rounded-full bg-gray-200'></div>
                    <div className='w-8 h-8 rounded-full bg-gray-200'></div>
                    <div className='w-8 h-8 rounded-full bg-gray-200'></div>
                  </div>
                </div>
              ))
            : referralList.map((referral) => {
                const maskedEmail = referral.email.replace(
                  /(.{1}).*(@.).*/,
                  (_, a, b) => `${a}****${b}****`
                );
                const invited = referral.isInvited;
                const signedUp = referral.isSignedUp;
                const qualified = referral.isQualified;

                return (
                  <div
                    key={referral.referralId}
                    className='border p-4 rounded-lg shadow-sm space-y-2 text-gray-800'
                  >
                    <div>
                      <span className='font-medium'>Date referred:</span>{' '}
                      {new Date(referral.invitedOn).toLocaleDateString()}
                    </div>
                    <div>
                      <span className='font-medium'>Email:</span> {maskedEmail}
                    </div>
                    <div className='flex gap-2 mt-2'>
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          invited
                            ? 'bg-[var(--primary-color)] text-white'
                            : 'bg-gray-200 text-gray-400'
                        }`}
                      >
                        <Mail size={16} />
                      </div>
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          signedUp
                            ? 'bg-[var(--primary-color)] text-white'
                            : 'bg-gray-200 text-gray-400'
                        }`}
                      >
                        <Check size={16} />
                      </div>
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          qualified
                            ? 'bg-[var(--primary-color)] text-white'
                            : 'bg-gray-200 text-gray-400'
                        }`}
                      >
                        <DollarSign size={16} />
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>

        <div className='mt-6'>
          {referralList.length >= limit && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageClick}
              getPaginationRange={() => getPaginationRange(currentPage, totalPages)}
            />
          )}
        </div>
      </div>
    </motion.div>
  );
};

export { ReferralHistory };
